import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { GridRowModes } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";

import axios from "axios";

import { API_URL, LIMIT } from "../../config/config.data";
import { dataActions } from "../../store";

// renders an autocomplete component for selecting project sites
export default function SelectProjectSites({
  api,
  id,
  field,
  value,
  row,
  column,
  rowModesModel,
  setRowModesModel,
}) {
  const dispatch = useDispatch();
  const originalSites = row.sites;
  const [open, setOpen] = useState(false);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  // get sites
  const getSites = async (abortController) => {
    try {
      const response = await axios.get(`${API_URL}/sites?limit=${LIMIT}`, {
        signal: abortController.signal,
      });

      setSites([
        ...response.data.sites.sort((a, b) => a.name.localeCompare(b.name)),
      ]);
    } catch (error) {
      console.error(error);
    }
  };

  // get projects
  const getProjects = async () => {
    axios
      .get(`${API_URL}/projects?limit=${LIMIT}`)
      .then((response) => {
        dispatch(
          dataActions.setData({
            data: response.data.projects,
            dataName: "projects",
          })
        );
        setLoading(false);
      })
      .catch((error) => {
        console.error("failed to get projects", error);
        setLoading(false);
      });
  };

  // add site to project
  const addSiteToProject = async (siteId) => {
    setLoading(true);
    try {
      axios
        .put(`${API_URL}/projects/${id}/sites/${siteId}`)
        .then((response) => {
          getProjects();
        })
        .catch((error) => {
          console.error("failed to add site to project", error);
          setLoading(false);
        });
    } catch (error) {
      console.error("failed to add site to project", error);
      setLoading(false);
    }
  };

  const deleteSiteFromProject = async (siteId) => {
    setLoading(true);
    try {
      axios
        .delete(`${API_URL}/projects/${id}/sites/${siteId}`)
        .then((response) => {
          getProjects();
        })
        .catch((error) => {
          console.error("failed to delete site from project", error);
          setLoading(false);
        });
    } catch (error) {
      console.error("failed to delete site from project", error);
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setRowModesModel({
      ...rowModesModel,
      [row.id]: { mode: GridRowModes.View },
    });
  };

  useEffect(() => {
    const abortController = new AbortController();
    getSites(abortController);
    return () => abortController.abort();
  }, []);

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <Typography
          sx={{
            width: "200px",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {originalSites.map((site) => site.name).join(", ")}
        </Typography>
        <IconButton onClick={() => setOpen(true)}>
          <EditIcon />
        </IconButton>
      </Box>
      <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          {`Edit ${field}`}
          {loading && <CircularProgress />}
        </DialogTitle>
        <DialogContent fullWidth>
          <Autocomplete
            fullWidth
            multiple
            id="sites"
            options={sites}
            limitTags={5}
            getOptionLabel={(option) =>
              `${option.name} (${option.site_number})`
            }
            defaultValue={originalSites}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(event, newValue) => {
              newValue.forEach((site) => {
                if (!originalSites.includes(site)) {
                  addSiteToProject(site.id);
                }
              });

              originalSites.forEach((site) => {
                if (!newValue.includes(site)) {
                  deleteSiteFromProject(site.id);
                }
              });
            }}
            renderInput={(params) => (
              <Box sx={{ my: 1 }}>
                <TextField {...params} label="Sites" />
              </Box>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCloseDialog}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
